import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  createFileApi,
  getFileApi,
  listFilesApi,
  updateFileApi,
  deleteFileApi,
} from '../../file';
import { rawAxios } from '../../common';

/**
 * Consolidated test component for all File endpoints
 */
const TestFiles: React.FC = () => {
  // Create File state
  const [createOrgId, setCreateOrgId] = useState('');
  const [createFileName, setCreateFileName] = useState('test-file.pdf');
  const [createFileSize, setCreateFileSize] = useState('1024');
  const [createFileType, setCreateFileType] = useState('application/pdf');
  const [createRequestUploadUrl, setCreateRequestUploadUrl] = useState(true);
  const [createResponse, setCreateResponse] = useState<any>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Get File state
  const [getFileId, setGetFileId] = useState('');
  const [getRequestDownloadUrl, setGetRequestDownloadUrl] = useState(false);
  const [getResponse, setGetResponse] = useState<any>(null);
  const [getLoading, setGetLoading] = useState(false);
  const [getError, setGetError] = useState<string | null>(null);

  // List Files state
  const [listOrgId, setListOrgId] = useState('');
  const [listLimit, setListLimit] = useState('20');
  const [listResponse, setListResponse] = useState<any>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Update File state
  const [updateFileId, setUpdateFileId] = useState('');
  const [updateFileName, setUpdateFileName] = useState('');
  const [updateResponse, setUpdateResponse] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Delete File state
  const [deleteFileId, setDeleteFileId] = useState('');
  const [deleteResponse, setDeleteResponse] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Upload to S3 state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Handlers
  const handleCreateFile = async () => {
    if (!createOrgId.trim() || !createFileName.trim() || !createFileSize.trim()) {
      setCreateError('Please enter org ID, file name and size');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      const result = await createFileApi({
        orgId: createOrgId.trim(),
        fileName: createFileName.trim(),
        sizeBytes: parseInt(createFileSize),
        fileType: createFileType.trim() || undefined,
        requestUploadUrl: createRequestUploadUrl,
      });
      setCreateResponse(result);

      // Save upload URL if provided
      if (result.uploadUrl) {
        setUploadUrl(result.uploadUrl.url);
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleGetFile = async () => {
    if (!getFileId.trim()) {
      setGetError('Please enter a file ID');
      return;
    }

    setGetLoading(true);
    setGetError(null);

    try {
      const result = await getFileApi({
        fileId: getFileId.trim(),
        requestDownloadUrl: getRequestDownloadUrl,
      });
      setGetResponse(result);
    } catch (err) {
      setGetError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetLoading(false);
    }
  };

  const handleListFiles = async () => {
    setListLoading(true);
    setListError(null);

    try {
      const result = await listFilesApi({
        orgId: listOrgId.trim() || undefined,
        limit: parseInt(listLimit) || 20,
      });
      setListResponse(result);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setListLoading(false);
    }
  };

  const handleUpdateFile = async () => {
    if (!updateFileId.trim() || !updateFileName.trim()) {
      setUpdateError('Please enter file ID and new file name');
      return;
    }

    setUpdateLoading(true);
    setUpdateError(null);

    try {
      const result = await updateFileApi({
        fileId: updateFileId.trim(),
        fileName: updateFileName.trim(),
      });
      setUpdateResponse(result);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteFileId.trim()) {
      setDeleteError('Please enter a file ID');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await deleteFileApi({ fileId: deleteFileId.trim() });
      setDeleteResponse({ message: 'File deleted successfully' });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUploadToS3 = async () => {
    if (!uploadFile || !uploadUrl) {
      setUploadError('Please select a file and generate an upload URL first');
      return;
    }

    setUploadLoading(true);
    setUploadError(null);

    try {
      const response = await rawAxios.put(uploadUrl, uploadFile, {
        headers: {
          'Content-Type': uploadFile.type || 'application/octet-stream',
        },
      });
      setUploadResponse({ status: response.status, message: 'File uploaded successfully' });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        File APIs
      </Typography>

      {/* Create File */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Create File</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID (Optional)"
              value={createOrgId}
              onChange={(e) => setCreateOrgId(e.target.value)}
              fullWidth
              placeholder="Leave empty for personal files"
            />
            <TextField
              label="File Name"
              value={createFileName}
              onChange={(e) => setCreateFileName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="File Size (bytes)"
              value={createFileSize}
              onChange={(e) => setCreateFileSize(e.target.value)}
              fullWidth
              type="number"
              required
            />
            <TextField
              label="File Type"
              value={createFileType}
              onChange={(e) => setCreateFileType(e.target.value)}
              fullWidth
              placeholder="e.g., application/pdf"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={createRequestUploadUrl}
                  onChange={(e) => setCreateRequestUploadUrl(e.target.checked)}
                />
              }
              label="Request Upload URL"
            />
            <Button
              variant="contained"
              onClick={handleCreateFile}
              disabled={createLoading}
            >
              {createLoading ? <CircularProgress size={24} /> : 'Create File'}
            </Button>
            {createResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(createResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {createError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{createError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Upload to S3 */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Upload File to S3</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Use the upload URL from "Create File" above
            </Typography>
            <TextField
              label="Upload URL"
              value={uploadUrl}
              onChange={(e) => setUploadUrl(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <Button
              variant="outlined"
              component="label"
            >
              Select File
              <input
                type="file"
                hidden
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </Button>
            {uploadFile && (
              <Typography variant="body2">
                Selected: {uploadFile.name} ({uploadFile.size} bytes)
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleUploadToS3}
              disabled={uploadLoading}
            >
              {uploadLoading ? <CircularProgress size={24} /> : 'Upload to S3'}
            </Button>
            {uploadResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(uploadResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {uploadError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{uploadError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Get File */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get File</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="File ID"
              value={getFileId}
              onChange={(e) => setGetFileId(e.target.value)}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={getRequestDownloadUrl}
                  onChange={(e) => setGetRequestDownloadUrl(e.target.checked)}
                />
              }
              label="Request Download URL"
            />
            <Button
              variant="contained"
              onClick={handleGetFile}
              disabled={getLoading}
            >
              {getLoading ? <CircularProgress size={24} /> : 'Get File'}
            </Button>
            {getResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(getResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {getError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* List Files */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">List Files</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID (Optional)"
              value={listOrgId}
              onChange={(e) => setListOrgId(e.target.value)}
              fullWidth
              placeholder="Leave empty to list all your files"
            />
            <TextField
              label="Limit"
              value={listLimit}
              onChange={(e) => setListLimit(e.target.value)}
              fullWidth
              type="number"
            />
            <Button
              variant="contained"
              onClick={handleListFiles}
              disabled={listLoading}
            >
              {listLoading ? <CircularProgress size={24} /> : 'List Files'}
            </Button>
            {listResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                  Files ({Object.keys(listResponse.files || {}).length}):
                </Typography>
                {listResponse.files && Object.keys(listResponse.files).length > 0 ? (
                  <List>
                    {Object.values(listResponse.files).map((file: any, index: number) => (
                      <React.Fragment key={file.fileId}>
                        <ListItem>
                          <ListItemText
                            primary={file.fileName}
                            secondary={
                              <>
                                ID: {file.fileId}
                                <br />
                                Size: {file.sizeBytes} bytes | Type: {file.fileType}
                                <br />
                                Owner: {file.ownerUserId}
                                {file.ownerOrgId && (
                                  <>
                                    <br />
                                    Org ID: {file.ownerOrgId}
                                  </>
                                )}
                                <br />
                                Created: {new Date(file.createdAt).toLocaleString()}
                              </>
                            }
                          />
                        </ListItem>
                        {index < Object.keys(listResponse.files).length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography>No files found</Typography>
                )}
              </Paper>
            )}
            {listError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{listError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update File */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update File</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="File ID"
              value={updateFileId}
              onChange={(e) => setUpdateFileId(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="New File Name"
              value={updateFileName}
              onChange={(e) => setUpdateFileName(e.target.value)}
              fullWidth
              required
            />
            <Button
              variant="contained"
              onClick={handleUpdateFile}
              disabled={updateLoading}
            >
              {updateLoading ? <CircularProgress size={24} /> : 'Update File'}
            </Button>
            {updateResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Delete File */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Delete File</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="File ID"
              value={deleteFileId}
              onChange={(e) => setDeleteFileId(e.target.value)}
              fullWidth
              required
            />
            <Button
              variant="contained"
              onClick={handleDeleteFile}
              disabled={deleteLoading}
              color="error"
            >
              {deleteLoading ? <CircularProgress size={24} /> : 'Delete File'}
            </Button>
            {deleteResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(deleteResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {deleteError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{deleteError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestFiles;
